/**
 * SugerirMejoraModal — el canal por el que un cliente pide una funcionalidad.
 *
 * ADR-0017. La idea no aparece navegando un board de votación: aparece acá
 * adentro, intentando hacer algo que no se puede. Por eso el formulario es
 * corto (dos campos) y no pide nada que ya sepamos.
 *
 * Lo que se guarda sin preguntarlo:
 *   · tenant y usuario  → de la sesión
 *   · rol               → de la sesión
 *   · ruta del panel    → de la navegación
 *
 * Esa última es la de mayor valor: es el contexto que nadie escribe y el que
 * convierte "no encuentro cómo hacer X" en una propuesta accionable.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@smarty/shared';

import { BlockStack, Box, Link, Text } from '@/lib/polaris';
import { ROUTES } from '@/config/routes.config';
import { AppModal } from '@/components/admin/AppModal';
import { AppTextField } from '@/components/admin/AppTextField';
import { AppBanner } from '@/components/admin/AppBanner';
import { useAdminToastOptional } from '@/contexts/AdminToastContext';
import { supabase } from '@/lib/supabase';

const LARGO_MIN_TITULO = 3;
const LARGO_MAX_TITULO = 160;
const LARGO_MAX_DESCRIPCION = 4000;

interface Props {
  open: boolean;
  onClose: () => void;
}

export function SugerirMejoraModal({ open, onClose }: Props) {
  const { tenantId, userRole } = useAuth();
  const { pathname } = useLocation();
  const toast = useAdminToastOptional();

  const [titulo, setTitulo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [errorTitulo, setErrorTitulo] = useState<string | undefined>();
  const [errorEnvio, setErrorEnvio] = useState<string | undefined>();
  const [guardando, setGuardando] = useState(false);

  // La ruta se congela al ABRIR, no al enviar: si el modal queda abierto
  // mientras se navega por detrás, el contexto que interesa sigue siendo la
  // pantalla donde a la persona se le ocurrió la idea.
  const rutaAlAbrir = useRef<string>(pathname);
  useEffect(() => {
    if (open) rutaAlAbrir.current = pathname;
  }, [open, pathname]);

  const limpiar = useCallback(() => {
    setTitulo('');
    setDescripcion('');
    setErrorTitulo(undefined);
    setErrorEnvio(undefined);
  }, []);

  const cerrar = useCallback(() => {
    if (guardando) return;
    limpiar();
    onClose();
  }, [guardando, limpiar, onClose]);

  const enviar = useCallback(async () => {
    const tituloLimpio = titulo.trim();

    if (tituloLimpio.length < LARGO_MIN_TITULO) {
      setErrorTitulo('Cuéntanos en una frase qué necesitas.');
      return;
    }
    if (!tenantId) {
      setErrorEnvio('No pudimos identificar tu negocio. Recarga la página e intentá de nuevo.');
      return;
    }

    setErrorTitulo(undefined);
    setErrorEnvio(undefined);
    setGuardando(true);

    const { error } = await supabase.from('feature_requests').insert({
      titulo: tituloLimpio,
      descripcion: descripcion.trim() || null,
      tenant_id: tenantId,
      rol_autor: userRole ?? null,
      ruta_panel: rutaAlAbrir.current,
    });

    setGuardando(false);

    if (error) {
      // El mensaje crudo de Postgres no le sirve a nadie: se registra para
      // nosotros y a la persona se le dice qué hacer.
      console.error('[propuestas] no se pudo guardar', error.message);
      setErrorEnvio('No se pudo enviar. Revisa tu conexión e intentá de nuevo.');
      return;
    }

    toast?.success('Gracias, lo tenemos', 'Leemos todas las propuestas.');
    limpiar();
    onClose();
  }, [titulo, descripcion, tenantId, userRole, toast, limpiar, onClose]);

  const restantes = LARGO_MAX_TITULO - titulo.length;

  return (
    <AppModal
      open={open}
      onClose={cerrar}
      title="Sugerir una mejora"
      primaryAction={{
        content: 'Enviar propuesta',
        onAction: enviar,
        loading: guardando,
        disabled: guardando,
      }}
      secondaryActions={[{ content: 'Cancelar', onAction: cerrar, disabled: guardando }]}
    >
      <AppModal.Section>
        <BlockStack gap="400">
          <Text as="p" variant="bodyMd" tone="subdued">
            ¿Qué te falta para trabajar más cómodo? Lo que más se pide es lo que
            construimos primero.
          </Text>

          {errorEnvio ? (
            <AppBanner tone="critical" title="No se pudo enviar">
              <Text as="p" variant="bodyMd">
                {errorEnvio}
              </Text>
            </AppBanner>
          ) : null}

          <AppTextField
            label="¿Qué necesitas?"
            value={titulo}
            onChange={(valor: string) => {
              setTitulo(valor.slice(0, LARGO_MAX_TITULO));
              if (errorTitulo) setErrorTitulo(undefined);
            }}
            error={errorTitulo}
            placeholder="Poder exportar los pedidos del mes a Excel"
            autoComplete="off"
            autoFocus
            maxLength={LARGO_MAX_TITULO}
            helpText={
              restantes <= 30 ? `Te quedan ${restantes} caracteres` : 'Una frase alcanza'
            }
          />

          <AppTextField
            label="¿Para qué lo usarías? (opcional)"
            value={descripcion}
            onChange={(valor: string) => setDescripcion(valor.slice(0, LARGO_MAX_DESCRIPCION))}
            multiline={4}
            autoComplete="off"
            placeholder="Cuéntanos el caso real: qué haces hoy para resolverlo y cuánto tiempo te lleva."
            helpText="Cuanto más concreto, más fácil nos resulta priorizarlo."
          />

          <Box>
            <BlockStack gap="200">
              <Text as="p" variant="bodySm" tone="subdued">
                Se envía junto con la pantalla en la que estás, para que no tengas
                que explicarnos dónde ocurre.
              </Text>
              {/* Salida hacia el board: quien viene a pedir algo suele querer ver
                  si ya lo pidió otro — y votarlo rinde más que duplicarlo. */}
              <Link url={ROUTES.PROPUESTAS} onClick={cerrar} removeUnderline>
                Ver qué están pidiendo otros negocios
              </Link>
            </BlockStack>
          </Box>
        </BlockStack>
      </AppModal.Section>
    </AppModal>
  );
}
